import React, { useEffect, useRef, useState, ReactNode, forwardRef, useImperativeHandle } from 'react';
import WaveSurfer from 'wavesurfer.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import { IconPlayerPlay, IconPlayerPause, IconPlayerSkipBack } from '@tabler/icons-react';

interface SoundSignalVisualizerProps {
    audioUrl: string;
    barsCount?: number;
    showBpmControls?: boolean;
    showPlaybackControls?: boolean;
    showExtraControls?: boolean;
    initialBpm?: number;
    onBpmChange?: (bpm: number) => void;
    onPlayStateChange?: (isPlaying: boolean) => void;
    className?: string;
    controlPanelClassName?: string;
    controlsExtra?: ReactNode;
    compact?: boolean;
    sections?: Array<{ start: number; end: number; label: string }>;
}

export interface SoundSignalVisualizerRef {
    play: () => void;
    pause: () => void;
    toggle: () => void;
    rewind: () => void;
    isPlaying: boolean;
}

export const SoundSignalVisualizer = forwardRef<SoundSignalVisualizerRef, SoundSignalVisualizerProps>(({
    audioUrl,
    barsCount = 8,
    showBpmControls = false,
    showPlaybackControls = true,
    showExtraControls = true,
    initialBpm = 120,
    onBpmChange,
    onPlayStateChange,
    className = '',
    controlPanelClassName = '',
    controlsExtra,
    compact = false,
    sections,
}, ref) => {
    const waveformRef = useRef<HTMLDivElement>(null);
    const topTimelineRef = useRef<HTMLDivElement>(null);
    const bottomTimelineRef = useRef<HTMLDivElement>(null);
    const wsRef = useRef<WaveSurfer | null>(null);
    const roRef = useRef<ResizeObserver | null>(null);

    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [bpm, setBpm] = useState(initialBpm);

    const effectiveBarsCount = sections && sections.length > 0 ? sections.length : barsCount;

    const [barLabels, setBarLabels] = useState<{ leftPx: number, label: string }[]>([]);
    const [sectionDividers, setSectionDividers] = useState<number[]>([]);
    useEffect(() => {
        const el = waveformRef.current;
        if (!el || duration === 0) return;

        const recompute = () => {
            const containerWidth = el.clientWidth;
            if (!containerWidth) return;
            const labels: { leftPx: number; label: string }[] = [];
            const dividers: number[] = [];

            if (sections && sections.length > 0) {
                sections
                    .filter(s => !isNaN(s.start) && !isNaN(s.end))
                    .filter(s => s.end >= 0 && s.start <= duration)
                    .forEach((section) => {
                        const clampedStart = Math.max(0, Math.min(duration, section.start));
                        const clampedEnd = Math.max(0, Math.min(duration, section.end));
                        const center = (clampedStart + clampedEnd) / 2;
                        const leftPx = (center / duration) * containerWidth;
                        labels.push({ leftPx, label: section.label });
                    });

                sections
                    .slice(0, -1)
                    .map(s => s.end)
                    .filter(t => !isNaN(t) && t > 0 && t < duration)
                    .forEach((time) => {
                        dividers.push((time / duration) * containerWidth);
                    });
            } else {
                const interval = duration / effectiveBarsCount;
                for (let i = 0; i < effectiveBarsCount; i++) {
                    const barTime = (i + 0.5) * interval;
                    const leftPx = (barTime / duration) * containerWidth;
                    labels.push({ leftPx, label: `Bar ${i + 1}` });
                }
            }

            setBarLabels(labels);
            setSectionDividers(dividers);
        };

        const schedule = () => requestAnimationFrame(recompute);
        schedule();

        roRef.current?.disconnect();
        roRef.current = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => schedule())
            : null;
        roRef.current?.observe(el);

        window.addEventListener('resize', schedule);
        return () => {
            roRef.current?.disconnect();
            window.removeEventListener('resize', schedule);
        };
    }, [duration, effectiveBarsCount, sections]);

    useEffect(() => {
        if (!waveformRef.current || !bottomTimelineRef.current) {
            return;
        }
        const prevTime = wsRef.current?.getCurrentTime?.() || 0;
        const wasPlaying = wsRef.current?.isPlaying?.() || false;
        wsRef.current?.destroy();
        wsRef.current = null;
        const audio = new Audio(audioUrl);
        audio.preload = 'metadata';
        const onLoadedMeta = () => {
            const dur = audio.duration;
            setDuration(dur);
            const tryInit = () => {
                const containerWidth = waveformRef.current?.clientWidth || 0;
                if (!containerWidth) {
                    requestAnimationFrame(tryInit);
                    return;
                }
                const pxPerSec = containerWidth / dur;

                let secInterval = Math.ceil(dur / 8);
                // @ts-ignore
                const bottom = TimelinePlugin.create({
                    container: bottomTimelineRef.current!,
                    timeInterval: secInterval,
                    formatTimeCallback: secs => {
                        if (secs < 1e-2) return '0s';
                        if (Math.abs(secs - dur) < secInterval / 2) return `${Math.round(dur)}s`;
                        return `${Math.round(secs)}s`;
                    },
                    style: {
                        borderTop: compact ? '1px solid rgba(148, 163, 184, 0.4)' : '1px solid #E5E7EB',
                        backgroundColor: 'transparent',
                        color: '#374151',
                        fontSize: compact ? '10px' : '12px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: '500',
                    },
                });

                const ws = WaveSurfer.create({
                    container: waveformRef.current!,
                    backend: 'WebAudio',
                    waveColor: '#BFC6D1',
                    progressColor: '#222B3A',
                    cursorColor: '#374151',
                    cursorWidth: 2,
                    barWidth: 2,
                    barGap: 1,
                    barRadius: 2,
                    height: compact ? 75 : 104,
                    normalize: true,
                    hideScrollbar: true,
                    // @ts-ignore
                    pixelRatio: window.devicePixelRatio || 1,
                    // @ts-ignore
                    minPxPerSec: pxPerSec,
                    plugins: [bottom],
                });
                wsRef.current = ws;
                ws.load(audioUrl);
                ws.on('audioprocess', (time) => {
                    setCurrentTime(time);
                });
                ws.on('finish', () => {
                    ws.seekTo(0);
                    setIsPlaying(false);
                    setCurrentTime(0);
                });
                ws.on('ready', () => {
                    if (prevTime > 0 && prevTime < dur) {
                        ws.seekTo(prevTime / dur);
                    }
                    if (wasPlaying) {
                        ws.play();
                    }
                });
            };

            tryInit();
        };
        audio.addEventListener('loadedmetadata', onLoadedMeta);

        const handleStopAllAudio = () => {
            if (wsRef.current) {
                wsRef.current.pause();
            }
        };

        window.addEventListener('stopAllAudio', handleStopAllAudio);

        return () => {
            audio.removeEventListener('loadedmetadata', onLoadedMeta);
            window.removeEventListener('stopAllAudio', handleStopAllAudio);
            wsRef.current?.pause();
            wsRef.current?.destroy();
            wsRef.current = null;
        };
    }, [audioUrl, effectiveBarsCount]);

    const m = Math.floor(duration / 60).toString().padStart(2, '0');
    const s = Math.floor(duration % 60).toString().padStart(2, '0');
    const cm = Math.floor(currentTime / 60).toString().padStart(2, '0');
    const cs = Math.floor(currentTime % 60).toString().padStart(2, '0');

    const togglePlay = () => {
        const ws = wsRef.current;
        if (!ws) return;
        ws.playPause();
        const newState = !isPlaying;
        setIsPlaying(newState);
        onPlayStateChange?.(newState);
    };

    const play = () => {
        const ws = wsRef.current;
        if (!ws || isPlaying) return;
        ws.play();
        setIsPlaying(true);
        onPlayStateChange?.(true);
    };

    const pause = () => {
        const ws = wsRef.current;
        if (!ws || !isPlaying) return;
        ws.pause();
        setIsPlaying(false);
        onPlayStateChange?.(false);
    };

    const rewind = () => {
        const ws = wsRef.current;
        if (!ws) return;
        const wasPlaying = ws.isPlaying();
        ws.seekTo(0);
        setCurrentTime(0);
        if (wasPlaying) {
            setTimeout(() => {
                ws.play();
                setIsPlaying(true);
                onPlayStateChange?.(true);
            }, 50);
        } else {
            setIsPlaying(false);
            onPlayStateChange?.(false);
        }
    };

    useImperativeHandle(ref, () => ({
        play,
        pause,
        toggle: togglePlay,
        rewind,
        isPlaying
    }));

    return (
        <div className={ `w-full max-w-full ${className}` }>
            <div className={compact ? "bg-card rounded-lg border border-border p-3" : "bg-card rounded-xl shadow-md border border-border p-8"}>
                <div className="relative">
                    <div
                        ref={ topTimelineRef }
                        className={compact ? "w-full h-5 mb-1" : "w-full h-7 mb-2"}
                        style={{
                            borderBottom: compact ? '1px solid rgba(148, 163, 184, 0.4)' : '1px solid #E5E7EB'
                        }}
                    />
                    <div style={ { pointerEvents: 'none', position: 'absolute', top: 0, left: 0, width: '100%', height: '28px' } }>
                        {sectionDividers.length > 0 && sectionDividers.map((leftPx, idx) => (
                            <div
                                key={`divider-top-${idx}`}
                                style={{
                                    position: 'absolute',
                                    left: leftPx,
                                    top: 4,
                                    bottom: 4,
                                    width: 1,
                                    backgroundColor: 'hsl(var(--muted-foreground) / 0.55)',
                                    zIndex: 10
                                }}
                            />
                        ))}
                        { barLabels.map((bar, idx) => (
                            <div key={ idx } style={ { position: 'absolute', left: bar.leftPx, top: 0, transform: 'translateX(-50%)', color: 'hsl(var(--card-foreground))', fontWeight: 500, fontSize: compact ? 11 : 14, fontFamily: 'Inter, sans-serif' } }>
                                { bar.label }
                            </div>
                        )) }
                    </div>
                    <div
                        ref={ waveformRef }
                        className={compact ? "w-full bg-accent rounded-md overflow-hidden" : "w-full bg-muted rounded-lg overflow-hidden"}
                    />
                    <div ref={ bottomTimelineRef } className={compact ? "w-full h-5 mt-1" : "w-full h-7 mt-2"} />
                </div>
                {compact ? (
                    <div className="flex justify-between items-center text-[11px] text-muted-foreground mt-1 font-mono">
                        <div className="flex items-center gap-2">
                            <span>{ `${cm}:${cs}` }</span>
                            {showPlaybackControls && (
                                <>
                                    <button
                                        onClick={togglePlay}
                                        className="w-7 h-7 rounded-full flex items-center justify-center bg-primary text-primary-foreground hover:opacity-90 transition-colors"
                                    >
                                        {isPlaying ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
                                    </button>
                                    <button
                                        onClick={rewind}
                                        className="w-7 h-7 rounded-full flex items-center justify-center border border-border text-card-foreground hover:bg-accent transition-colors"
                                    >
                                        <IconPlayerSkipBack size={14} />
                                    </button>
                                </>
                            )}
                        </div>
                        <span>{ `${m}:${s}` }</span>
                    </div>
                ) : (
                    <div className="flex justify-between text-sm text-muted-foreground mt-4 font-mono">
                        <span>{ `${cm}:${cs}` }</span>
                        <span>{ `${m}:${s}` }</span>
                    </div>
                )}
            </div>
            { (!compact && (showPlaybackControls || showBpmControls || showExtraControls)) && (
                <div className={ `bg-card rounded-xl shadow-md border border-border p-8 flex items-center gap-6 mt-8 ${controlPanelClassName}` }>
                    { showPlaybackControls && (
                        <>
                            <button
                                onClick={ togglePlay }
                                className="w-14 h-14 bg-[#1A1A2E] text-white rounded-full flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg focus:outline-none focus:ring-2 focus:ring-primary border border-[#2A2A4A]"
                            >
                                { isPlaying ? <IconPlayerPause size={ 28 } /> : <IconPlayerPlay size={ 28 } /> }
                            </button>
                            <button
                                onClick={ rewind }
                                className="w-14 h-14 border border-border text-card-foreground rounded-full flex items-center justify-center hover:bg-accent transition-colors shadow focus:outline-none focus:ring-2 focus:ring-border"
                            >
                                <IconPlayerSkipBack size={ 28 } />
                            </button>
                        </>
                    ) }
                    { showBpmControls && (
                        <>
                            <span className="ml-6 text-lg text-card-foreground font-medium">Controls</span>
                            <div className="flex items-center gap-4 ml-auto">
                                <button
                                    onClick={ () => {
                                        const newBpm = bpm > 40 ? bpm - 10 : 40;
                                        setBpm(newBpm);
                                        onBpmChange?.(newBpm);
                                    } }
                                    className="w-12 h-12 rounded-md bg-muted text-2xl text-muted-foreground flex items-center justify-center hover:bg-accent transition"
                                >
                                    –
                                </button>
                                <span className="text-2xl font-semibold text-card-foreground min-w-[80px] text-center">{ bpm } <span className="text-base font-medium">BPM</span></span>
                                <button
                                    onClick={ () => {
                                        const newBpm = bpm < 300 ? bpm + 10 : 300;
                                        setBpm(newBpm);
                                        onBpmChange?.(newBpm);
                                    } }
                                    className="w-12 h-12 rounded-md bg-muted text-2xl text-muted-foreground flex items-center justify-center hover:bg-accent transition"
                                >
                                    +
                                </button>
                            </div>
                        </>
                    ) }
                    { showExtraControls && controlsExtra && (
                        <div className="flex items-center gap-2 ml-auto">{ controlsExtra }</div>
                    ) }
                </div>
            ) }
        </div>
    );
}); 